'use client';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  rating: number;
  comment: string;
  image: string;
  imageAlt: string;
  location: string;
}

const testimonials: Testimonial[] = [
{
  id: 'testimonial_sarah',
  name: 'Sarah Johnson',
  role: 'Business Consultant',
  rating: 5,
  comment: 'ScootRide has transformed my daily commute. The scooters are well-maintained, easy to find, and so much fun to ride. I save time and money while reducing my carbon footprint!',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_143abf91c-1763295980237.png",
  imageAlt: 'Professional woman with brown hair smiling in business attire',
  location: 'New York, NY'
},
{
  id: 'testimonial_michael',
  name: 'Michael Chen',
  role: 'Software Engineer',
  rating: 5,
  comment: 'Perfect for short trips around the city. The app is intuitive, scooters are always available near me, and the pricing is transparent. The premium scooters are incredibly smooth!',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1add37a33-1763296420971.png",
  imageAlt: 'Young Asian man with glasses wearing casual shirt outdoors',
  location: 'San Francisco, CA'
},
{
  id: 'testimonial_emily',
  name: 'Emily Rodriguez',
  role: 'Marketing Director',
  rating: 5,
  comment: 'I love how eco-friendly and convenient ScootRide is. No more sitting in traffic or searching for parking. Just unlock, ride, and park. It\'s the future of urban mobility!',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1345fbf99-1763293684642.png",
  imageAlt: 'Woman with long dark hair smiling confidently in professional setting',
  location: 'Miami, FL'
}];


export default function TestimonialCards() {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  return (
    <section className="py-24 bg-gradient-to-b from-white to-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <span className="inline-block px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-body font-semibold mb-4">
            Customer Stories
          </span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
            What Our Riders Say
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust ScootRide for their daily transportation needs.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) =>
          <div
            key={testimonial.id}
            className={`glass p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all cursor-pointer reveal reveal-delay-${index * 100}`}
            onMouseEnter={() => setHoveredCard(testimonial.id)}
            onMouseLeave={() => setHoveredCard(null)}>
            
              {/* Rating Stars */}
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) =>
              <Icon
                key={`${testimonial.id}_star_${i}`}
                name="StarIcon"
                variant="solid"
                size={18}
                className="text-accent" />

              )}
              </div>

              {/* Comment */}
              <p className="text-foreground/80 font-body text-sm leading-relaxed mb-6">
                "{testimonial.comment}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4 pt-4 border-t border-border">
                <div className="relative w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                  src={testimonial.image}
                  alt={testimonial.imageAlt}
                  className="w-full h-full object-cover" />
                
                  {hoveredCard === testimonial.id &&
                <div className="absolute inset-0 bg-accent/20" />
                }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-primary text-sm truncate">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-muted-foreground font-body truncate">
                    {testimonial.role}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Icon name="MapPinIcon" size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-body">
                      {testimonial.location}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Trust Badge */}
        <div className="text-center mt-16 reveal reveal-delay-300">
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-success/10 border border-success/20">
            <Icon name="CheckBadgeIcon" variant="solid" size={24} className="text-success" />
            <span className="text-sm font-body font-semibold text-success">
              Verified Reviews from Real Customers
            </span>
          </div>
        </div>
      </div>
    </section>);

}